import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useSetAccountCustomerServiceRep } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';

import type { CustomerServiceRep } from '~/services/LuxLoyaltyCustomerServiceRepService';

interface Props {
  open: boolean;
  onClose: () => void;
  customerId: string;
  memberLabel: string;
  /** Currently assigned rep id, used to grey-out the current selection. */
  currentRepId: string | null;
  /** All reps available for selection. */
  reps: ReadonlyArray<CustomerServiceRep>;
}

/** Reassign a single member's concierge to a different rep. */
export default function AssignmentReassignDialog({
  open,
  onClose,
  customerId,
  memberLabel,
  currentRepId,
  reps,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [newRepId, setNewRepId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNewRepId(null);
    }
  }, [open]);

  const setRepMutation = useSetAccountCustomerServiceRep({
    onSuccess: () => {
      enqueueSnackbar(`Reassigned ${memberLabel}`, { variant: 'success' });
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to reassign: ${error.message}`, { variant: 'error' });
    },
  });

  const canSubmit = !!newRepId && newRepId !== currentRepId && !setRepMutation.isPending;

  return (
    <Dialog open={open} onClose={setRepMutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reassign representative</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Choose a new representative for <strong>{memberLabel}</strong>.
          </Typography>
          <TextField
            select
            label="New representative"
            value={newRepId ?? ''}
            onChange={(e) => setNewRepId(e.target.value ? String(e.target.value) : null)}
            fullWidth
            disabled={setRepMutation.isPending}
          >
            {reps.map((rep) => (
              <MenuItem key={rep.id} value={rep.id} disabled={rep.id === currentRepId}>
                {rep.firstName} {rep.lastName} — {rep.email}
                {rep.id === currentRepId ? ' (current)' : ''}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={setRepMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (newRepId) {
              setRepMutation.mutate({ customerId, customerServiceRepId: newRepId });
            }
          }}
          disabled={!canSubmit}
          startIcon={setRepMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Reassign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
